import { useState } from "react";

import { QuestionType } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { QuestionScope } from "../../types";
import { COMMUNICATION_PREFERENCES_TYPE, FormValues, ResponseFormatType } from "../types";

export const useScopeManager = (options: {
  form: UseFormReturn<FormValues>;
  defaultScope: QuestionScope;
  isEditing: boolean;
  countAlreadyAnswered: number;
  getDefaultQuestionTitle: (type: ResponseFormatType, scope: QuestionScope) => string | undefined;
}) => {
  const { form, defaultScope, isEditing, countAlreadyAnswered, getDefaultQuestionTitle } = options;

  const [displayedScope, setDisplayedScope] = useState<QuestionScope>(defaultScope);
  const [pendingScopeChange, setPendingScopeChange] = useState<QuestionScope | null>(null);

  const commitScopeChange = (newScope: QuestionScope) => {
    setDisplayedScope(newScope);
    form.setValue("scope", newScope);

    if (newScope === "buyer") {
      form.setValue("ratesAssigned", []);

      if (form.getValues("type") === QuestionType.Name) {
        form.setValue("type", QuestionType.Simple);
        if (!isEditing) {
          form.setValue("question", "");
        }
      }
    } else if (newScope === "participant") {
      form.setValue("askForAllRates", true);

      if (form.getValues("type") === COMMUNICATION_PREFERENCES_TYPE) {
        form.setValue("type", QuestionType.Simple);
      }
    }

    const currentType = form.getValues("type");
    const defaultTitle = getDefaultQuestionTitle(currentType, newScope);

    const hasFixedQuestionTitle =
      newScope === "participant" && (currentType === QuestionType.Name || currentType === QuestionType.Email);

    if (defaultTitle && (!isEditing || hasFixedQuestionTitle)) {
      form.setValue("question", defaultTitle);
    }
  };

  const handleScopeChange = (newScope: QuestionScope) => {
    if (countAlreadyAnswered > 0 && newScope !== displayedScope) {
      setPendingScopeChange(newScope);
      return;
    }
    commitScopeChange(newScope);
  };

  const resetScope = () => {
    setDisplayedScope(defaultScope);
    setPendingScopeChange(null);
  };

  return {
    displayedScope,
    pendingScopeChange,
    setPendingScopeChange,
    handleScopeChange,
    commitScopeChange,
    resetScope,
  };
};
